import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Plus, Pencil, Trash2, Info, Euro, X } from "lucide-react";

const MESI = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const MODALITA = ["Bonifico", "Contanti", "Assegno", "Carta", "Altro"];
const eur = (n) => (Number(n) || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

export default function SediTerritoriali() {
  const { user } = useAuth();
  const isStaff = user.role === "owner" || user.role === "collaboratore";
  const now = new Date();
  const empty = { partner_id: "", importo: "", data_pagamento: "", modalita: "Bonifico", mese: now.getMonth() + 1, anno: now.getFullYear(), stato: "non_pagato", note: "" };

  const [items, setItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [dialog, setDialog] = useState(null); // {id?, form}
  const [saving, setSaving] = useState(false);
  const [flt, setFlt] = useState({ partner_id: "all", mese: "all", anno: "all" });

  const load = () => api.get("/sedi-contributi").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (isStaff) api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const anni = useMemo(() => Array.from(new Set(items.map((r) => r.anno).filter(Boolean))).sort((a, b) => b - a), [items]);
  const filtered = useMemo(() => items.filter((r) =>
    (flt.partner_id === "all" || r.partner_id === flt.partner_id) &&
    (flt.mese === "all" || String(r.mese) === flt.mese) &&
    (flt.anno === "all" || String(r.anno) === flt.anno)
  ), [items, flt]);
  const resetFlt = () => setFlt({ partner_id: "all", mese: "all", anno: "all" });
  const hasFlt = flt.partner_id !== "all" || flt.mese !== "all" || flt.anno !== "all";

  const openNew = () => setDialog({ id: null, form: { ...empty } });
  const openEdit = (r) => setDialog({ id: r.id, form: { partner_id: r.partner_id, importo: r.importo, data_pagamento: r.data_pagamento || "", modalita: r.modalita || "Bonifico", mese: r.mese, anno: r.anno, stato: r.stato, note: r.note || "" } });
  const setF = (k, v) => setDialog((d) => ({ ...d, form: { ...d.form, [k]: v } }));

  const save = async () => {
    const f = dialog.form;
    if (!f.partner_id) { toast.error("Seleziona la sede (partner)"); return; }
    setSaving(true);
    const payload = { ...f, importo: parseFloat(f.importo) || 0, mese: parseInt(f.mese), anno: parseInt(f.anno) };
    try {
      if (dialog.id) await api.put(`/sedi-contributi/${dialog.id}`, payload);
      else await api.post("/sedi-contributi", payload);
      toast.success(dialog.id ? "Contributo aggiornato" : "Contributo registrato");
      setDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm("Eliminare questo contributo?")) return;
    try { await api.delete(`/sedi-contributi/${r.id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const totPagato = useMemo(() => filtered.filter((r) => r.stato === "pagato").reduce((s, r) => s + (Number(r.importo) || 0), 0), [filtered]);
  const totSospeso = useMemo(() => filtered.filter((r) => r.stato !== "pagato").reduce((s, r) => s + (Number(r.importo) || 0), 0), [filtered]);
  const totPeriodo = totPagato + totSospeso;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="sedi-territoriali-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><MapPin className="h-7 w-7 text-dusty-600" /> Sedi Territoriali</h1>
          <p className="text-sm text-slate-500 mt-1">Contributo economico mensile per sede. Dato a solo titolo informativo.</p>
        </div>
        {isStaff && (
          <Button onClick={openNew} className="bg-dusty-600 hover:bg-dusty-700 text-white" data-testid="sede-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Registra contributo
          </Button>
        )}
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="sedi-info-banner">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <span>Questi importi sono <b>solo informativi</b>: non rientrano nel registro vendite, nello stato pagamenti, nelle royalty dei partner, né negli export economici.</span>
      </div>

      <Card className="border-slate-200 shadow-sm p-4" data-testid="sedi-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {isStaff && (
            <div>
              <Label className="text-xs">Partner (sede)</Label>
              <Select value={flt.partner_id} onValueChange={(v) => setFlt((s) => ({ ...s, partner_id: v }))}>
                <SelectTrigger className="mt-1.5 bg-white" data-testid="sedi-filter-partner"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le sedi</SelectItem>
                  {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Mese</Label>
            <Select value={flt.mese} onValueChange={(v) => setFlt((s) => ({ ...s, mese: v }))}>
              <SelectTrigger className="mt-1.5 bg-white" data-testid="sedi-filter-mese"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i mesi</SelectItem>
                {MESI.slice(1).map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Anno</Label>
            <Select value={flt.anno} onValueChange={(v) => setFlt((s) => ({ ...s, anno: v }))}>
              <SelectTrigger className="mt-1.5 bg-white" data-testid="sedi-filter-anno"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli anni</SelectItem>
                {anni.map((a) => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            {hasFlt && <Button variant="ghost" size="sm" onClick={resetFlt} className="text-slate-500" data-testid="sedi-filter-reset"><X className="h-3.5 w-3.5 mr-1" /> Azzera filtri</Button>}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4 border-slate-200"><div className="text-xs text-slate-500 uppercase tracking-wider">Totale periodo</div><div className="text-2xl font-bold text-slate-800 mt-1" data-testid="sedi-tot-periodo">{eur(totPeriodo)}</div><div className="text-xs text-slate-400 mt-0.5">{filtered.length} contributi</div></Card>
        <Card className="p-4 border-slate-200"><div className="text-xs text-slate-500 uppercase tracking-wider">Pagato</div><div className="text-2xl font-bold text-emerald-600 mt-1" data-testid="sedi-tot-pagato">{eur(totPagato)}</div></Card>
        <Card className="p-4 border-slate-200"><div className="text-xs text-slate-500 uppercase tracking-wider">Non pagato</div><div className="text-2xl font-bold text-amber-600 mt-1" data-testid="sedi-tot-sospeso">{eur(totSospeso)}</div></Card>
      </div>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100 bg-slate-50/60">
                <th className="py-3 px-4 font-medium">Sede (Partner)</th>
                <th className="py-3 px-4 font-medium">Mese</th>
                <th className="py-3 px-4 font-medium text-right">Importo</th>
                <th className="py-3 px-4 font-medium">Modalità</th>
                <th className="py-3 px-4 font-medium">Data pagamento</th>
                <th className="py-3 px-4 font-medium">Stato</th>
                <th className="py-3 px-4 font-medium">Note</th>
                {isStaff && <th className="py-3 px-4 font-medium text-right">Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`sede-row-${i}`}>
                  <td className="py-2.5 px-4 font-medium text-slate-800">{r.partner_nome || "—"}</td>
                  <td className="py-2.5 px-4 text-slate-600">{MESI[r.mese] || r.mese} {r.anno}</td>
                  <td className="py-2.5 px-4 text-right font-medium text-slate-800">{eur(r.importo)}</td>
                  <td className="py-2.5 px-4 text-slate-600">{r.modalita || "—"}</td>
                  <td className="py-2.5 px-4 text-slate-600">{r.data_pagamento ? r.data_pagamento.split("-").reverse().join("-") : "—"}</td>
                  <td className="py-2.5 px-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${r.stato === "pagato" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid={`sede-stato-${i}`}>
                      {r.stato === "pagato" ? "Pagato" : "Non pagato"}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-slate-500 max-w-[220px] truncate" title={r.note}>{r.note || "—"}</td>
                  {isStaff && (
                    <td className="py-2.5 px-4 text-right">
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        <button onClick={() => openEdit(r)} className="text-slate-500 hover:text-dusty-700" title="Modifica" data-testid={`sede-edit-${i}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => remove(r)} className="text-slate-400 hover:text-rose-500" title="Elimina" data-testid={`sede-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={isStaff ? 8 : 7} className="py-14 text-center text-slate-400" data-testid="sedi-empty">{items.length === 0 ? "Nessun contributo registrato." : "Nessun contributo per i filtri selezionati."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!dialog} onOpenChange={(v) => { if (!v) setDialog(null); }}>
        <DialogContent className="bg-white max-w-lg" data-testid="sede-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{dialog?.id ? "Modifica contributo" : "Registra contributo"}</DialogTitle>
            <DialogDescription>Dato a solo titolo informativo, non incide sui conteggi economici.</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-1">
              <div className="sm:col-span-2">
                <Label>Sede (Partner)</Label>
                <Select value={dialog.form.partner_id} onValueChange={(v) => setF("partner_id", v)}>
                  <SelectTrigger className="mt-1.5 bg-white" data-testid="sede-form-partner"><SelectValue placeholder="Seleziona partner" /></SelectTrigger>
                  <SelectContent>
                    {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Importo (€)</Label>
                <Input type="number" step="0.01" value={dialog.form.importo} onChange={(e) => setF("importo", e.target.value)} className="mt-1.5 bg-white" data-testid="sede-form-importo" />
              </div>
              <div>
                <Label>Modalità</Label>
                <Select value={dialog.form.modalita} onValueChange={(v) => setF("modalita", v)}>
                  <SelectTrigger className="mt-1.5 bg-white" data-testid="sede-form-modalita"><SelectValue /></SelectTrigger>
                  <SelectContent>{MODALITA.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Mese di riferimento</Label>
                <Select value={String(dialog.form.mese)} onValueChange={(v) => setF("mese", v)}>
                  <SelectTrigger className="mt-1.5 bg-white" data-testid="sede-form-mese"><SelectValue /></SelectTrigger>
                  <SelectContent>{MESI.slice(1).map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Anno</Label>
                <Input type="number" value={dialog.form.anno} onChange={(e) => setF("anno", e.target.value)} className="mt-1.5 bg-white" data-testid="sede-form-anno" />
              </div>
              <div>
                <Label>Data pagamento</Label>
                <Input type="date" value={dialog.form.data_pagamento} onChange={(e) => setF("data_pagamento", e.target.value)} className="mt-1.5 bg-white" data-testid="sede-form-data" />
              </div>
              <div>
                <Label>Stato</Label>
                <Select value={dialog.form.stato} onValueChange={(v) => setF("stato", v)}>
                  <SelectTrigger className="mt-1.5 bg-white" data-testid="sede-form-stato"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non_pagato">Non pagato</SelectItem>
                    <SelectItem value="pagato">Pagato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>Note</Label>
                <Textarea value={dialog.form.note} onChange={(e) => setF("note", e.target.value)} className="mt-1.5 bg-white" rows={2} data-testid="sede-form-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-dusty-600 hover:bg-dusty-700 text-white" data-testid="sede-form-save">{saving ? "Salvo…" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
